import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { Brief, TaskDetail } from "@/types";
import { api, apiStream } from "@/services/api";

export const useBriefStore = defineStore("briefs", () => {
  const briefs = ref<Brief[]>([]);
  const active = ref<Brief | null>(null);
  const activeTask = ref<TaskDetail | null>(null);
  const loading = ref(false);
  const agentProgress = ref("");
  const streamedReply = ref("");
  const submitted = computed(() =>
    briefs.value.filter((brief) => brief.status !== "draft"),
  );

  async function loadList() {
    briefs.value = (await api<{ briefs: Brief[] }>("/briefs")).briefs;
  }
  async function open(id: number) {
    active.value = (await api<{ brief: Brief }>(`/briefs/${id}`)).brief;
  }
  async function create(title = "未命名 Brief") {
    active.value = (
      await api<{ brief: Brief }>("/briefs", {
        method: "POST",
        body: JSON.stringify({ title }),
      })
    ).brief;
    await loadList();
  }
  async function sendRequirement(content: string) {
    if (!active.value) return;
    const brief = active.value;
    const temporaryMessageId = -Date.now();
    brief.messages.push({
      id: temporaryMessageId,
      role: "user",
      content,
      created_at: new Date().toISOString(),
    });
    loading.value = true;
    agentProgress.value = "正在发送需求";
    streamedReply.value = "";
    try {
      await apiStream(
        `/briefs/${brief.id}/messages/stream`,
        { content },
        (event) => {
          if (event.type === "progress") agentProgress.value = event.label;
          if (event.type === "delta") streamedReply.value += event.text;
          if (event.type === "complete") active.value = event.brief as Brief;
        },
      );
      await loadList();
    } catch (error) {
      if (active.value?.id === brief.id)
        active.value.messages = active.value.messages.filter(
          (message) => message.id !== temporaryMessageId,
        );
      throw error;
    } finally {
      loading.value = false;
      agentProgress.value = "";
      streamedReply.value = "";
    }
  }
  async function renameActive(title: string) {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(`/briefs/${active.value.id}/title`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      })
    ).brief;
    await loadList();
  }
  async function deleteBrief(id: number) {
    await api(`/briefs/${id}`, { method: "DELETE" });
    briefs.value = briefs.value.filter((brief) => brief.id !== id);
    if (active.value?.id === id) active.value = null;
  }
  async function confirmTask(taskId: number, confirmed: boolean) {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(
        `/briefs/${active.value.id}/tasks/${taskId}/confirm`,
        { method: "POST", body: JSON.stringify({ confirmed }) },
      )
    ).brief;
  }
  async function submit() {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(`/briefs/${active.value.id}/submit`, {
        method: "POST",
      })
    ).brief;
    await loadList();
  }
  async function createChange(content: string) {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(`/briefs/${active.value.id}/changes`, {
        method: "POST",
        body: JSON.stringify({ content }),
      })
    ).brief;
  }
  async function openTask(id: number) {
    activeTask.value = (await api<{ task: TaskDetail }>(`/tasks/${id}`)).task;
  }
  async function refresh() {
    if (active.value) await open(active.value.id);
  }
  return {
    briefs,
    active,
    activeTask,
    loading,
    agentProgress,
    streamedReply,
    submitted,
    loadList,
    open,
    create,
    sendRequirement,
    renameActive,
    deleteBrief,
    confirmTask,
    submit,
    createChange,
    openTask,
    refresh,
  };
});
